import { useEffect, useState } from 'react';
import { useAdminLocale } from '../lib/admin-locale.js';
import { ApiError } from '../lib/api.js';
import { readJson, useRemote } from '../lib/remote.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Field,
  Icon,
  Modal,
  Select,
  TextInput,
  Textarea,
} from '../components/ui/index.js';

type EmailFormat = 'TEXT' | 'HTML';
type EmailCategory = 'action' | 'business' | 'security' | 'ops';

interface EmailTemplate {
  key: string;
  category: EmailCategory;
  name: string;
  description: string;
  format: EmailFormat;
  subject: string;
  textBody: string;
  htmlBody: string;
  enabled: boolean;
  variables: string[];
  isCustom: boolean;
}

interface EmailBrand {
  productName: string;
  accentColor: string;
  logoUrl: string;
  adminUrl: string;
}

interface EmailList {
  items: EmailTemplate[];
  brand: EmailBrand;
}

const CATEGORY_LABEL: Record<EmailCategory, readonly [string, string]> = {
  action: ['Ενέργεια χρήστη', 'User action'],
  business: ['Επιχείρηση', 'Business'],
  security: ['Ασφάλεια', 'Security'],
  ops: ['Λειτουργία', 'Operations'],
};

const CATEGORY_ORDER: EmailCategory[] = ['action', 'business', 'security', 'ops'];

export function EmailTemplatesPanel() {
  const { text } = useAdminLocale();
  const remote = useRemote<EmailList>('/v1/platform/email-templates', null);
  const [selected, setSelected] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const current = remote.data?.items.find((item) => item.key === selected) ?? null;

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-[6] mb-1 border-b border-slate-200 bg-slate-50/95 py-3 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {text('Κείμενα email', 'Email content')}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          {text(
            'Ένα πρότυπο ανά γραμμή. «Ενεργό» σημαίνει ότι επιτρέπεται η αποστολή. Στο HTML το όνομα, το χρώμα και το λογότυπο έρχονται από την Εμφάνιση. Η αποθήκευση γίνεται μέσα σε κάθε πρότυπο.',
            'One template per row. “Active” means sending is enabled. In HTML, the name, color, and logo come from Appearance. Save changes within each template.',
          )}
        </p>
      </div>
      {remote.data ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {text('Τρέχον brand:', 'Current brand:')}{' '}
          <span className="font-medium text-slate-800 dark:text-slate-200">
            {remote.data.brand.productName}
          </span>
          <span
            className="ml-2 inline-block size-3 rounded-full align-middle"
            style={{ background: remote.data.brand.accentColor }}
          />
          <span className="ml-1">{remote.data.brand.accentColor}</span>
        </p>
      ) : null}
      {notice ? (
        <p
          className={
            notice.tone === 'ok'
              ? 'text-sm text-emerald-700 dark:text-emerald-300'
              : 'text-sm text-rose-700 dark:text-rose-300'
          }
        >
          {notice.text}
        </p>
      ) : null}
      {remote.error ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{remote.error.message}</p>
      ) : null}
      {!remote.data && !remote.error ? (
        <p className="text-sm text-slate-500">{text('Φόρτωση προτύπων…', 'Loading templates…')}</p>
      ) : null}
      {remote.data
        ? CATEGORY_ORDER.map((category) => {
            const items = remote.data?.items.filter((item) => item.category === category) ?? [];
            if (items.length === 0) return null;
            return (
              <Card
                key={category}
                title={text(...CATEGORY_LABEL[category])}
                description={text(`${items.length} πρότυπα`, `${items.length} templates`)}
                flush
              >
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {items.map((item) => (
                    <li
                      key={item.key}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {item.name}
                          {item.isCustom ? (
                            <span className="ml-2 text-xs font-normal text-amber-700 dark:text-amber-300">
                              {text('προσαρμοσμένο', 'custom')}
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                          {item.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge value={item.enabled ? 'ACTIVE' : 'DISABLED'} />
                        <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {item.format === 'HTML' ? 'HTML' : text('Κείμενο', 'Text')}
                        </span>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => setSelected(item.key)}
                        >
                          <Icon name="eye" className="size-4" /> {text('Επεξεργασία', 'Edit')}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            );
          })
        : null}
      {current ? (
        <TemplateEditor
          template={current}
          onClose={() => setSelected(null)}
          onSaved={(message) => {
            setNotice({ tone: 'ok', text: message });
            void remote.mutate();
          }}
        />
      ) : null}
    </div>
  );
}

function TemplateEditor(props: {
  template: EmailTemplate;
  onClose: () => void;
  onSaved: (text: string) => void;
}) {
  const { text } = useAdminLocale();
  const [format, setFormat] = useState<EmailFormat>(props.template.format);
  const [subject, setSubject] = useState(props.template.subject);
  const [textBody, setTextBody] = useState(props.template.textBody);
  const [htmlBody, setHtmlBody] = useState(props.template.htmlBody);
  const [enabled, setEnabled] = useState(props.template.enabled);
  const [preview, setPreview] = useState<{
    subject: string;
    text: string;
    html: string | null;
  } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const draft = { format, subject, textBody, htmlBody, enabled };

  useEffect(() => {
    let cancelled = false;
    void readJson<{ subject: string; text: string; html: string | null }>(
      `/v1/platform/email-templates/${props.template.key}/preview`,
      { method: 'POST', body: JSON.stringify(draft) },
      null,
    )
      .then((next) => {
        if (!cancelled) setPreview(next);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
    // Preview tracks the open template and format. Body edits refresh from the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.template.key, format]);

  async function refreshPreview(): Promise<void> {
    const next = await readJson<{ subject: string; text: string; html: string | null }>(
      `/v1/platform/email-templates/${props.template.key}/preview`,
      { method: 'POST', body: JSON.stringify(draft) },
      null,
    );
    setPreview(next);
  }

  async function save(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      await readJson(
        `/v1/platform/email-templates/${props.template.key}`,
        { method: 'PATCH', body: JSON.stringify(draft) },
        null,
      );
      props.onSaved(
        text(
          `Το πρότυπο «${props.template.name}» αποθηκεύτηκε.`,
          `Template “${props.template.name}” was saved.`,
        ),
      );
      props.onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : text('Η αποθήκευση απέτυχε.', 'Save failed.'),
      );
    } finally {
      setPending(false);
    }
  }

  async function reset(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const item = await readJson<EmailTemplate>(
        `/v1/platform/email-templates/${props.template.key}/reset`,
        { method: 'POST' },
        null,
      );
      setFormat(item.format);
      setSubject(item.subject);
      setTextBody(item.textBody);
      setHtmlBody(item.htmlBody);
      setEnabled(item.enabled);
      props.onSaved(
        text(
          `Το πρότυπο «${props.template.name}» επέστρεψε στην προεπιλογή.`,
          `Template “${props.template.name}” was reset to its default.`,
        ),
      );
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : text('Η επαναφορά απέτυχε.', 'Reset failed.'),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <Modal
      wide
      title={props.template.name}
      description={props.template.description}
      onClose={props.onClose}
    >
      <div className="grid gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <Field label={text('Μορφή', 'Format')}>
            <Select
              className="mt-1.5 min-w-40"
              value={format}
              onChange={(next) => setFormat(next === 'TEXT' ? 'TEXT' : 'HTML')}
              options={[
                { value: 'HTML', label: 'HTML' },
                { value: 'TEXT', label: text('Απλό κείμενο', 'Plain text') },
              ]}
            />
          </Field>
          <Checkbox
            boxed
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            label={text('Ενεργό', 'Active')}
            description={
              enabled
                ? text(
                    'Στέλνεται όταν συμβεί το αντίστοιχο γεγονός.',
                    'Sent when the corresponding event occurs.',
                  )
                : text('Δεν στέλνεται, όσο μένει ανενεργό.', 'Not sent while inactive.')
            }
          />
        </div>
        <Field label={text('Θέμα', 'Subject')}>
          <TextInput value={subject} onChange={(event) => setSubject(event.target.value)} />
        </Field>
        <Field
          label={text('Απλό κείμενο', 'Plain text')}
          hint={text(
            'Στέλνεται πάντα, και όταν η μορφή είναι HTML.',
            'Always sent, including when the format is HTML.',
          )}
        >
          <Textarea
            rows={6}
            value={textBody}
            onChange={(event) => setTextBody(event.target.value)}
          />
        </Field>
        {format === 'HTML' ? (
          <Field
            label={text('HTML περιεχόμενο', 'HTML content')}
            hint={text(
              'Όχι ολόκληρο έγγραφο. Το πλαίσιο, το χρώμα και το λογότυπο μπαίνουν γύρω από αυτό.',
              'Not a complete document. The frame, color, and logo are added around it.',
            )}
          >
            <Textarea
              rows={8}
              value={htmlBody}
              onChange={(event) => setHtmlBody(event.target.value)}
              className="font-mono text-xs"
            />
          </Field>
        ) : null}
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {text('Μεταβλητές:', 'Variables:')}{' '}
          {props.template.variables.length > 0
            ? props.template.variables.map((name) => `{{${name}}}`).join(', ')
            : text('μόνο brand', 'brand only')}
          {' · '}
          Brand: {'{{productName}}'}, {'{{accentColor}}'}, {'{{onAccent}}'}, {'{{adminUrl}}'}
        </p>
        {error ? <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" disabled={pending} onClick={() => void reset()}>
            {text('Επαναφορά', 'Reset')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => void refreshPreview().catch(() => setPreview(null))}
          >
            {text('Προεπισκόπηση', 'Preview')}
          </Button>
          <Button type="button" variant="secondary" onClick={props.onClose}>
            <Icon name="x" className="size-4" /> {text('Κλείσιμο', 'Close')}
          </Button>
          <Button type="button" disabled={pending} onClick={() => void save()}>
            <Icon name="save" className="size-4" />
            {pending ? text('Αποθήκευση…', 'Saving…') : text('Αποθήκευση', 'Save')}
          </Button>
        </div>
        {preview ? (
          <div className="space-y-2 border-t border-slate-100 pt-4 dark:border-slate-800">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {text('Προεπισκόπηση', 'Preview')} · {preview.subject}
            </p>
            {preview.html ? (
              <iframe
                title={text('Προεπισκόπηση email', 'Email preview')}
                sandbox=""
                srcDoc={preview.html}
                className="h-80 w-full rounded-lg border border-slate-200 bg-white dark:border-slate-700"
              />
            ) : (
              <pre className="max-h-80 overflow-auto rounded-lg bg-slate-50 p-3 text-sm whitespace-pre-wrap text-slate-800 dark:bg-slate-900 dark:text-slate-200">
                {preview.text}
              </pre>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
